import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import { addFuzzy, divideFuzzy, fractionToFuzzy, multiplyFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import { approximateFractionValue } from '@mimir/common/util/integerMath';

export interface FirstFramePtsDelay {
  aacPackets?: number;
  frames?: number;
  accuracyWarning?: string;
  fuzzBias?: number;
  roundingBias?: number;
  audioSampleRate?: number;
}

export interface FramePtsConversion {
  delay: FirstFramePtsDelay;
  frameDuration: FuzzyValue;
  minimumFrameIndex?: number;
}

export interface FrameTime {
  frameIndex: number;
  frameDuration: FuzzyValue;
  rounded: boolean;
  accuracyWarning?: string;
}

const aacPacketSamples = 1024;

function getPtsDelayTime(ptsDelay: FirstFramePtsDelay, frameDuration: FuzzyValue) {
  const aacPackets = fractionToFuzzy({ numerator: ptsDelay.aacPackets ?? 0, denominator: 1}, ptsDelay.accuracyWarning);
  const aacPacketDuration = { numerator: aacPacketSamples, denominator: ptsDelay.audioSampleRate ?? 48000 };
  const aacDelay = multiplyFuzzy(fractionToFuzzy(aacPacketDuration), aacPackets);
  const frames =  fractionToFuzzy({ numerator: ptsDelay.frames ?? 0, denominator: 1}, ptsDelay.accuracyWarning);
  const frameDelay = multiplyFuzzy(frameDuration, frames);
  return addFuzzy(aacDelay, frameDelay);
}

/**
 * Convert a Presentation Timestamp (PTS) to a frame time description which
 * identifies a specific video frame.
 */
export function ptsToFrameTime(pts: FuzzyValue, conversion: FramePtsConversion): FrameTime {
  const ptsDelayTime = getPtsDelayTime(conversion.delay, conversion.frameDuration);
  const mediaPosition = subtractFuzzy(pts, ptsDelayTime);
  const frameTime = divideFuzzy(mediaPosition, conversion.frameDuration);
  let frameIndex: number;
  let rounded = true;
  if (frameTime.precise) {
    if (frameTime.precise.denominator == 1) {
      frameIndex = frameTime.precise.numerator;
      rounded = false;
    } else {
      frameIndex = Math.floor(approximateFractionValue(frameTime.precise));
    }
  } else {
    frameIndex = Math.floor(frameTime.approximate + (conversion.delay.roundingBias ?? 0));
  }

  const minIndex = conversion.minimumFrameIndex ?? 0;
  let warning: string | undefined;
  if (frameIndex < minIndex) {
    warning = 'Frame ' + frameIndex + ' is not presentable';
    frameIndex = minIndex;
  }
  return {
    frameIndex,
    frameDuration: conversion.frameDuration,
    rounded,
    accuracyWarning: frameTime.accuracyWarning ?? warning
  };
}

export function frameTimeToSourceMediaPosition(frameTime: FrameTime): FuzzyValue {
  const index = fractionToFuzzy({ numerator: frameTime.frameIndex, denominator: 1});
  return multiplyFuzzy(frameTime.frameDuration, index);
}

export function sourceMediaPositionToPts(mediaPosition: FuzzyValue, frameDuration: FuzzyValue, delay: FirstFramePtsDelay) {
  const ptsDelayTime = getPtsDelayTime(delay, frameDuration);
  const result = addFuzzy(mediaPosition, ptsDelayTime);
  if (delay.fuzzBias) {
    result.approximate += delay.fuzzBias;
  }
  return result;
}

/**
  Convert a frame time description identifying a specific video frame to a
  Presentation Timestamp (PTS) for that frame.
*/
export function frameTimeToPts(frameTime: FrameTime, delay: FirstFramePtsDelay) {
  const mediaPosition = frameTimeToSourceMediaPosition(frameTime);
  return sourceMediaPositionToPts(mediaPosition, frameTime.frameDuration, delay);
}

/**
 * Convert a Presentation Timestamp (PTS) to an approximate browser media time
 * value suitable for setting on the currentTime property of video and audio
 * elements.
 *
 * A small bias is added to the time value to address numeric rounding issues
 * related to representing media time as floating point values.
 */
export function ptsToApproximateBrowserCurrentTime(framePts: FuzzyValue) {
  return framePts.approximate;
}

/**
 * Convert a browser media time value to a Presentation Timestamp (PTS). Since
 * the media time values you can obtain from video and audio browser elements
 * are represented as floating point values, the resulting Presentation
 * Timestamp denotes the value as approximate value, rather than a precise
 * fraction.
 *
 * Note that the time values and by extension PTS values used by browser do not
 * necessarily assign the first frame a time value of zero. Various media file
 * format and browser decoder issues can cause the PTS value of the first frame
 * to be delayed. Such delays can be described by FirstFramePtsDelay, and
 * ptsToFrameTime() should be used for converting PTS values to concrete frame
 * indexes.
 */
export function browserCurrentTimeToPts(mediaTime: number): FuzzyValue {
  return { approximate: mediaTime };
}

